import { SUPPORTED_LANGUAGES, normalizeLanguageCode } from '@/utils/language';

/**
 * Normalize URL by removing trailing slash and language prefix
 */
const normalizeUrl = (url: string): string => {
  if (url.endsWith('/')) {
    url = url.slice(0, -1);
  }

  // Remove language prefix if present (e.g., /en/blog -> /blog)
  const filteredUrlParts = url.split('/').filter(Boolean);
  if (
    filteredUrlParts.length > 0 &&
    filteredUrlParts[0] &&
    SUPPORTED_LANGUAGES.includes(normalizeLanguageCode(filteredUrlParts[0]))
  ) {
    url = '/' + filteredUrlParts.slice(1).join('/');
  }

  return url;
};

/**
 * Check for special route patterns that have specific page names
 */
const getSpecialRoutePage = (url: string): string | null => {
  // Define patterns that map to specific page names
  const specialPatterns: Array<{ prefix: string; pageName: string }> = [
    { prefix: '/blog/', pageName: 'BlogPost' },
    { prefix: '/medical-procedure/', pageName: 'MedicalProcedureDetail' },
    { prefix: '/treatment/', pageName: 'TreatmentDetail' },
    { prefix: '/equipment/', pageName: 'EquipmentDetail' },
    { prefix: '/provider/', pageName: 'ProviderDetail' },
    { prefix: '/doctor/', pageName: 'DoctorDetail' },
  ];

  // Check slug patterns (exactly 3 parts: /prefix/slug)
  for (const { prefix, pageName } of specialPatterns) {
    if (url.startsWith(prefix) && url.split('/').length === 3) {
      return pageName;
    }
  }

  // Partner dashboard nested routes should map to PartnerDashboard
  if (url.startsWith('/partner-dashboard')) {
    return 'PartnerDashboard';
  }

  return null;
};

/**
 * Extract the last part of the URL for page matching
 */
const extractUrlLastPart = (url: string): string => {
  const pathParts = url.split('/');
  const urlLastPartRaw = pathParts.at(-1);
  if (!urlLastPartRaw || urlLastPartRaw === '') {
    return '';
  }

  let urlLastPart = urlLastPartRaw;
  if (urlLastPart.includes('?')) {
    const querySplit = urlLastPart.split('?');
    urlLastPart = querySplit[0] ?? urlLastPart;
  }

  return urlLastPart;
};

/**
 * Convert kebab-case to PascalCase
 */
const kebabToPascal = (str: string): string => {
  return str
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join('');
};

/**
 * Find page name from available pages list
 */
const findPageName = (urlLastPart: string): string | undefined => {
  const pages: string[] = [
    'Home',
    'Auth',
    'Clinics',
    'ClinicDetail',
    'Treatments',
    'Equipments',
    'Blog',
    'TreatmentDetail',
    'BlogPostDetail',
    'AdminClinics',
    'AdminDashboard',
    'BlogPost',
    'UserDashboard',
    'PartnerDashboard',
    'Providers',
    'ProviderDetail',
    'ProvidersLocation',
    'PrivacyPolicy',
    'TermsConditions',
    'CookiePolicy',
    'MedicalDisclaimer',
    'AntiDiscrimination',
    'Doctors',
    'DoctorDetail',
    'MedicalProcedureDetail',
    'EquipmentDetail',
    'ThankYou',
    'AboutUs',
    'HowItWorks',
    'MedipathsLab',
    'Glossary',
    'Subprocessors',
    'AIChatbotPage',
    'Favorites',
    'NotFound',
  ];

  // Try exact match first (case-insensitive)
  let pageName = pages.find(page => page.toLowerCase() === urlLastPart.toLowerCase());

  // If not found, try converting kebab-case to PascalCase
  if (!pageName && urlLastPart.includes('-')) {
    const pascalCase = kebabToPascal(urlLastPart);
    pageName = pages.find(page => page.toLowerCase() === pascalCase.toLowerCase());
  }

  return pageName;
};

/**
 * Detects the current page name from a URL path
 * @param url - The URL path to analyze
 * @returns Page name or default page name
 */
export const getCurrentPage = (url: string): string => {
  const normalizedUrl = normalizeUrl(url);

  // Check for special route patterns first
  const specialPage = getSpecialRoutePage(normalizedUrl);
  if (specialPage) {
    return specialPage;
  }

  // Extract the last part of the URL for page matching
  const urlLastPart = extractUrlLastPart(normalizedUrl);
  if (!urlLastPart) {
    return 'Home';
  }

  // Find page name from available pages
  const pageName = findPageName(urlLastPart);

  return pageName ?? 'Home';
};
